import useAdvanceTable from 'hooks/useAdvanceTable';
import React, { useEffect, useState } from 'react'
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Card, Col, Row, Form, Alert } from 'react-bootstrap';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import { Link } from 'react-router-dom';
import paths from 'routes/paths';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'helpers/api';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import SubtleBadge from 'components/common/SubtleBadge';
import ScheduleBadge from 'components/inventory/ScheduleBadge';
import {
    FacilityInventoryModal,
    getSortedSchedules
} from 'components/navbar/top/InventoryConfig';

const SCHEDULE_DISPLAY = {
    '2': '2',
    '3': '3',
    '4': '4',
    '5': '5',
    '-999': 'Non Controlled',
    'otc': 'OTC'
};

// Flat, searchable text for the tracked-schedules column.
const buildScheduleLabel = (schedules) => {
    const list = getSortedSchedules(
        (schedules || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
    );
    return list.length > 0 ? list.map(s => SCHEDULE_DISPLAY[s] || s).join(', ') : 'Not set';
};

const FacilityTableView = (props) => {
    const { id } = props
    const [communicationOption, setCommunicationOptions] = useState([]);
    const [group, setGroup] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [inventoryFacility, setInventoryFacility] = useState(null);
    const [showInventoryModal, setShowInventoryModal] = useState(false);

    const openInventory = (facility) => {
        setInventoryFacility(facility);
        setShowInventoryModal(true);
    };
    const columns = [
        {
            accessorKey: 'facility_name',
            header: 'Faciity',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
            cell: ({ row: { original } }) => {
                return <Link to={{ pathname: paths.editfaciliitylookupform }} state={{ obj: original }} >
                    {original.facility_name}</Link>
            }
        },
        {
            accessorKey: 'email',
            header: 'Email',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
            cell: ({ row: { original } }) => {
                return original.is_email ? (<FontAwesomeIcon icon="check" className="ms-1 text-success " />) : (<FontAwesomeIcon icon="check" className="ms-1 opacity-50" />)
            }
        },
        {
            accessorKey: 'fax',
            header: 'Fax',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
            cell: ({ row: { original } }) => {
                return original.is_fax ? (<FontAwesomeIcon icon="check" className="ms-1 text-success " />) : (<FontAwesomeIcon icon="check" className="ms-1 opacity-50" />)
            }
        },
        {
            accessorKey: 'sms',
            header: 'SMS',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
            cell: ({ row: { original } }) => {
                return original.is_sms ? (<FontAwesomeIcon icon="check" className="ms-1 text-success " />) : (<FontAwesomeIcon icon="check" className="ms-1 opacity-50" />)
            }
        },
        {
            accessorKey: 'liaison',
            header: 'Liaison',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
            cell: ({ row: { original } }) => {
                return original.is_liaison ? (<FontAwesomeIcon icon="check" className="ms-1 text-success " />) : (<FontAwesomeIcon icon="check" className="ms-1 opacity-50" />)
            }
        },
        {
            accessorKey: 'cycle_leed_day',
            header: 'Lead Days',
            meta: {
                headerProps: { className: 'text-900' }
            }
        },
        {
            accessorKey: 'delivery_time',
            header: 'Delivery Time',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            }
        },
        {
            accessorKey: 'inventory_status',
            header: 'Inventory',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
            cell: ({ row: { original } }) => {
                return <SubtleBadge
                    bg={Number(original.inventory_enabled) === 1 ? 'success' : 'secondary'}
                    pill
                    className="d-inline-block">
                    {original.inventory_status}
                </SubtleBadge>
            }
        },
        {
            accessorKey: 'inventory_schedules_label',
            header: 'Tracked Schedules',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
            cell: ({ row: { original } }) => {
                const list = original.inventory_schedules_list || [];
                if (list.length === 0) {
                    return <span className="text-500 fst-italic">Not set</span>
                }
                return <div className="d-flex flex-wrap gap-1">
                    {list.map(s => (
                        <ScheduleBadge key={s} token={s} />
                    ))}
                </div>
            }
        },
        {
            accessorKey: 'inventory_action',
            header: 'Action',
            enableSorting: false,
            meta: {
                headerProps: { className: 'text-900' },
                cellProps: { className: 'text-center' }
            },
            cell: ({ row: { original } }) => {
                return <OverlayTrigger placement="top" overlay={<Tooltip>Configure inventory</Tooltip>}>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="py-1 px-3 fs-11 rounded-pill"
                        onClick={() => openInventory(original)}>
                        Configure
                    </Button>
                </OverlayTrigger>
            }
        }
    ];

    const communicationmethodOptionsData = [
        {
            "key": "Email",
            "value": 1
        },
        {
            "key": "Fax",
            "value": 2
        },
        {
            "key": "SMS",
            "value": 3
        }
    ]
    useEffect(() => {
        fetchFacilityData()
    }, [])

    async function fetchFacilityData() {
        setLoading(true);
        let tokenStr = localStorage.getItem("accessToken");
        setCommunicationOptions([
            { key: 'Select', value: '' },
            ...communicationmethodOptionsData
        ])
        try{
            const { data } = await axios.get(`${process.env.REACT_APP_SERVER_URL}/facilities/list-facility/` + id, { headers: { 'Authorization': `Bearer ${tokenStr}` } });
            let objData = data.data
            let results = [];
            Object.values(objData).forEach(value => results.push({
                id: value.id,
                facility_name: value.facility_name,
                is_email: value.is_email,
                is_fax: value.is_fax,
                is_sms: value.is_sms,
                is_liaison: value.is_liaison,
                cycle_leed_day: value.cycle_leed_day,
                delivery_time: value.delivery_time,
                group_id: value.group_id,
                inventory_enabled: value.inventory_enabled,
                inventory_status: Number(value.inventory_enabled) === 1 ? 'Enabled' : 'Disabled',
                inventory_schedules: value.inventory_schedules,
                inventory_schedules_list: getSortedSchedules(
                    (value.inventory_schedules || '')
                        .split(',')
                        .map(s => s.trim().toLowerCase())
                        .filter(Boolean)
                ),
                inventory_schedules_label: buildScheduleLabel(value.inventory_schedules),
            }));
            // results.push(objData);
            setFilteredData(results);
            setGroup(results);

        }catch (error) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }

    const handleFieldChange = e => {
        const propsData = e.target.value;
        let filteredFacility;
        if (propsData == "1") {
            filteredFacility = group.filter(facility => Number(facility.is_email) === 1);
            setFilteredData(filteredFacility);
        }
        else if (propsData == "2") {
            filteredFacility = group.filter(facility => Number(facility.is_fax) === 2);
            setFilteredData(filteredFacility);
        }
        else if (propsData == "3") {
            filteredFacility = group.filter(facility => Number(facility.is_fax) === 3);
            setFilteredData(filteredFacility);
        } else {
            setFilteredData(group);
        }
    };

    const table = useAdvanceTable({
        data: filteredData,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });

    return (
        <AdvanceTableProvider {...table}>
            <div className="mb-3">
                <Card.Body className="p-3">
                    <Row className='mt-3 justify-content-between'>
                        <Col xs="4" className="mb-2">
                            <Form.Group>
                                <Form.Label>Communication Method</Form.Label>
                                <Form.Select
                                    name="communicationmethod"
                                    onChange={handleFieldChange}
                                    size="sm"
                                >
                                    {communicationOption?.map((option) => {
                                        return (
                                            <option key={option.value} value={option.value}>
                                                {option.key}
                                            </option>
                                        );
                                    })}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col xs="4" className="mt-4">
                            <AdvanceTableSearchBox placeholder="Search..." />
                        </Col>
                    </Row>
                    <Row>
                        <Col></Col>
                        <Col></Col>
                    </Row>
                    <Row>
                        {loading ? <ShimmerUI/> : filteredData.length > 0 ? (<Col xs="12" className="mb-1 mt-1">
                            <AdvanceTable
                                headerClassName="bg-200 text-nowrap align-middle text-center"
                                rowClassName="text-nowrap align-middle text-center"
                                tableProps={{
                                    size: 'sm',
                                    striped: true,
                                    className: 'fs-10 mb-0 overflow-hidden'
                                }}
                            />
                        </Col>) : <Alert variant="info" className="text-center mt-3">No Data Found</Alert>}
                    </Row>
                </Card.Body>
                <Card.Footer>
                    <div className="mt-3">
                        <AdvanceTableFooter
                            rowInfo
                            navButtons
                            rowsPerPageSelection
                        />
                    </div>
                </Card.Footer>
            </div>
            <FacilityInventoryModal
                show={showInventoryModal}
                facility={inventoryFacility}
                groupId={id}
                onHide={() => setShowInventoryModal(false)}
                onSaved={() => {
                    setShowInventoryModal(false);
                    fetchFacilityData();
                }}
            />
        </AdvanceTableProvider>
    )
}

export default FacilityTableView
